import { useContext, useMemo } from 'react';
import TransitChargeState from '~/constants/vdp/TransitChargeState';
import UserVehicleContext, {
  useVehiclePaymentTerms
} from '~/context/vdp/UserVehicleContext';
import { PricingError } from '~/services/common/contracts/PricingResponse';

export type TransitChargeInfo = {
  charge: number;
  isLoaded: boolean;
  isLoading: boolean;
  state: TransitChargeState;
};

export default function useTransitChargeInfo(): TransitChargeInfo {
  const { vehiclePricingInfo, vehiclePricingError } =
    useContext(UserVehicleContext);
  const {
    downPaymentDetails: { nonRefundableShippingFees } = {},
    transportCost
  } = useVehiclePaymentTerms();

  return useMemo<TransitChargeInfo>(() => {
    const state = (() => {
      if (vehiclePricingInfo.hasError || !vehiclePricingInfo.isLoaded) {
        return TransitChargeState.Unknown;
      }

      const hasValidFee = (fee: number | null | undefined) =>
        fee != null && fee > 0;

      if (
        nonRefundableShippingFees === undefined &&
        transportCost === undefined
      ) {
        return TransitChargeState.Unknown;
      }

      if (
        hasValidFee(nonRefundableShippingFees) ||
        hasValidFee(transportCost)
      ) {
        return TransitChargeState.HasCharge;
      }

      return TransitChargeState.NoCharge;
    })();

    return {
      charge:
        vehiclePricingError === PricingError.NoVehiclePricingInfo
          ? transportCost ?? 0
          : nonRefundableShippingFees ?? 0,
      isLoaded: vehiclePricingInfo.isLoaded,
      isLoading: vehiclePricingInfo.isLoading,
      state
    };
  }, [
    nonRefundableShippingFees,
    transportCost,
    vehiclePricingError,
    vehiclePricingInfo.hasError,
    vehiclePricingInfo.isLoaded,
    vehiclePricingInfo.isLoading
  ]);
}
